import {
  type DevicesListState,
  devicesListState,
  MIN_PASSWORD_LENGTH,
  parseUserAgent,
} from '@flashka/shared/auth';
import { useResendCooldown } from '@flashka/shared/react/use-resend-cooldown';
import {
  Button,
  Checkbox,
  CodeInput,
  Input,
  Skeleton,
  SkeletonPulse,
} from '@flashka/shared/ui';

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Separator } from '@/components/ui/separator';
import { toast } from '@/lib/flashka-toast';

import { authClient, clearSessionToken } from '../client';
import { useAuthAction } from '../use-auth-action';

// `listAccounts` (better-auth 1.6.x) returns rows shaped per its OpenAPI
// schema: `{ id, providerId, accountId, userId, createdAt, updatedAt,
// scopes }`. Only `providerId` is consumed here — 'credential' for the
// password-based account, 'google' / 'apple' for linked socials.
interface AccountRow {
  id: string;
  providerId: string;
}

function useAccounts() {
  const [accounts, setAccounts] = useState<AccountRow[] | null>(null);
  async function reload() {
    const { data } = await authClient.listAccounts();
    setAccounts((data as AccountRow[] | null) ?? []);
  }
  useEffect(() => {
    void reload();
  }, []);
  return { accounts, reload };
}

function PasswordSection({
  hasPassword,
  email,
  onPasswordSet,
}: {
  hasPassword: boolean;
  email: string;
  onPasswordSet: () => void;
}) {
  const { t } = useTranslation();
  const { busy, errorKey, run, setErrorKey } = useAuthAction();
  const [openForm, setOpenForm] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [revokeOthers, setRevokeOthers] = useState(true);
  // set-password (social-only account): forgot-password OTP flow reused as
  // the "prove you own this inbox" step, since there's no current password.
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const resend = useResendCooldown();

  async function change(e: FormEvent) {
    e.preventDefault();
    // Client-side length check first — `changePassword` fails the same
    // validation server-side, but catching it here is instant and gives the
    // clearer `weakPassword` copy without a round trip.
    if (next.length < MIN_PASSWORD_LENGTH) {
      setErrorKey('auth.errors.weakPassword');
      return;
    }
    if (
      await run(() =>
        authClient.changePassword({
          currentPassword: current,
          newPassword: next,
          revokeOtherSessions: revokeOthers,
        }),
      )
    ) {
      setOpenForm(false);
      setCurrent('');
      setNext('');
      toast(t('auth.security.passwordChanged'));
    }
  }

  async function sendSetOtp() {
    const alreadyOnOtpScreen = otpStep;
    if (await run(() => authClient.emailOtp.requestPasswordReset({ email }))) {
      resend.start();
      setOtp('');
      setOtpStep(true);
    } else if (!alreadyOnOtpScreen) {
      // The very first send failed (e.g. rate-limited) before the OTP screen
      // ever opened. Don't strand the user on a bare hint with no way to
      // retry — drop back to the top-level button so they can click it
      // again; the mapped error still renders below the section.
      setOpenForm(false);
    }
  }

  function startSetPassword() {
    setOpenForm(true);
    void sendSetOtp();
  }

  async function submitSet(e: FormEvent) {
    e.preventDefault();
    // Check BEFORE calling resetPassword: the endpoint verifies (and
    // consumes) the OTP before validating password length, so a too-short
    // password here would burn a correct code and leave the user stuck.
    if (next.length < MIN_PASSWORD_LENGTH) {
      setErrorKey('auth.errors.weakPassword');
      return;
    }
    if (
      await run(() => authClient.emailOtp.resetPassword({ email, otp, password: next }))
    ) {
      setOtpStep(false);
      setOpenForm(false);
      setNext('');
      toast(t('auth.security.passwordChanged'));
      onPasswordSet();
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{t('auth.security.passwordTitle')}</h3>
        {!openForm && (
          <Button
            variant="secondary"
            disabled={busy}
            onPress={() => (hasPassword ? setOpenForm(true) : startSetPassword())}>
            {hasPassword
              ? t('auth.security.changePassword')
              : t('auth.security.setPassword')}
          </Button>
        )}
      </div>

      {openForm && hasPassword && (
        <form onSubmit={change} className="flex flex-col gap-3">
          <Input
            label={t('auth.fields.currentPassword')}
            value={current}
            onChangeText={setCurrent}
            webProps={{
              type: 'password',
              autoComplete: 'current-password',
              required: true,
            }}
          />
          <Input
            label={t('auth.fields.newPassword')}
            value={next}
            onChangeText={setNext}
            webProps={{ type: 'password', autoComplete: 'new-password', required: true }}
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={revokeOthers}
              onCheckedChange={setRevokeOthers}
              accessibilityLabel={t('auth.security.revokeOthers')}
            />
            {t('auth.security.revokeOthers')}
          </label>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy || !current || !next}
            webProps={{ type: 'submit' }}>
            {t('auth.security.changePassword')}
          </Button>
        </form>
      )}

      {openForm && !hasPassword && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {t('auth.security.setPasswordHint')}
          </p>
          {otpStep && (
            <form onSubmit={submitSet} className="flex flex-col items-center gap-3">
              <CodeInput
                value={otp}
                onChangeText={setOtp}
                accessibilityLabel={t('auth.fields.code')}
              />
              <Input
                label={t('auth.fields.newPassword')}
                value={next}
                onChangeText={setNext}
                webProps={{
                  type: 'password',
                  autoComplete: 'new-password',
                  required: true,
                }}
              />
              <Button
                variant="primary"
                fullWidth
                loading={busy}
                disabled={busy || otp.length < 6 || !next}
                webProps={{ type: 'submit' }}>
                {t('auth.security.setPassword')}
              </Button>
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                disabled={!resend.ready || busy}
                onClick={() => void sendSetOtp()}>
                {resend.ready
                  ? t('auth.otp.resend')
                  : t('auth.otp.resendIn', { seconds: resend.remaining })}
              </button>
            </form>
          )}
        </div>
      )}
      {errorKey ? (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      ) : null}
    </section>
  );
}

function ConnectedAccountsSection({
  accounts,
  hasPassword,
  reload,
}: {
  accounts: AccountRow[];
  hasPassword: boolean;
  reload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { busy, errorKey, run } = useAuthAction();
  const providers: Array<'google' | 'apple'> = ['google', 'apple'];
  const linked = new Set(accounts.map((a) => a.providerId));
  const socialCount = accounts.filter((a) => a.providerId !== 'credential').length;

  async function link(provider: 'google' | 'apple') {
    await run(async () => {
      const { data, error } = await authClient.linkSocial({
        provider,
        // Absolute, not relative: the auth worker resolves this URL against
        // its OWN origin, not the app's — a bare pathname would redirect
        // post-OAuth back to the worker instead of the app.
        callbackURL: window.location.origin + window.location.pathname,
        errorCallbackURL: window.location.origin + window.location.pathname,
      });
      if (data?.url) window.location.assign(data.url);
      return { error };
    });
  }

  async function unlink(provider: 'google' | 'apple') {
    // better-auth >= 1.7 unlinks by the account ROW id, not by providerId.
    const row = accounts.find((a) => a.providerId === provider);
    if (!row) return;
    if (await run(() => authClient.unlinkAccount({ accountId: row.id }))) {
      await reload();
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-medium">{t('auth.security.connectedTitle')}</h3>
      {providers.map((p) => {
        const isLinked = linked.has(p);
        // Unlinking the only social account with no password would lock the
        // user out; the server blocks it too, but don't offer it at all.
        const lastMethod = isLinked && !hasPassword && socialCount <= 1;
        return (
          <div key={p} className="flex items-center justify-between">
            <span className="text-sm capitalize">{p}</span>
            {isLinked ? (
              lastMethod ? (
                <span className="text-xs text-muted-foreground">
                  {t('auth.security.lastMethodHint')}
                </span>
              ) : (
                <Button
                  variant="secondary"
                  loading={busy}
                  disabled={busy}
                  onPress={() => void unlink(p)}>
                  {t('auth.security.disconnect')}
                </Button>
              )
            ) : (
              <Button
                variant="secondary"
                loading={busy}
                disabled={busy}
                onPress={() => void link(p)}>
                {t('auth.security.connect')}
              </Button>
            )}
          </div>
        );
      })}
      {errorKey ? (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      ) : null}
    </section>
  );
}

// `listSessions` (better-auth 1.6.x) returns rows shaped per its OpenAPI
// schema: `{ id, createdAt, updatedAt, userId, expiresAt, token, ipAddress?,
// userAgent? }`. Only the fields the UI needs are declared here — the cast
// below is a narrowing (source→target) cast, same pattern as `AccountRow`.
interface SessionRow {
  id: string;
  token: string;
  userAgent?: string | null;
  createdAt: string | Date;
}

function DevicesSection() {
  const { t } = useTranslation();
  const { data } = authClient.useSession();
  const { busy, errorKey, run } = useAuthAction();
  const [state, setState] = useState<DevicesListState<SessionRow> | null>(null);

  async function reload() {
    try {
      // The four answers `list-sessions` can give are told apart in shared
      // (`devicesListState`). The one this used to get wrong: a session older
      // than the auth worker's `freshAge` (1 day) is refused with
      // `403 SESSION_NOT_FRESH`, and this rendered that as an EMPTY list.
      setState(devicesListState<SessionRow>(await authClient.listSessions()));
    } catch {
      // better-fetch REJECTS on a network failure instead of resolving `{ error }`.
      setState({ kind: 'error' });
    }
  }
  useEffect(() => {
    void reload();
  }, []);

  if (state === null) {
    // Same heading + three row-sized placeholders so the section holds its
    // final footprint while `listSessions()` resolves, instead of popping in.
    return (
      <section className="flex flex-col gap-2">
        <h3 className="font-medium">{t('auth.security.devicesTitle')}</h3>
        <SkeletonPulse className="gap-2" accessibilityLabel={t('common.loading')}>
          {[0, 1, 2].map((i) => (
            <DeviceRowSkeleton key={i} />
          ))}
        </SkeletonPulse>
      </section>
    );
  }

  // `useSession()`'s `session.token` and each `listSessions()` row's
  // `token` are the same underlying session-table field (Better Auth docs:
  // "Session table" — `token` doubles as the session cookie value), so a
  // direct string comparison reliably identifies the active session.
  const currentToken = data?.session?.token;

  async function revoke(token: string) {
    if (await run(() => authClient.revokeSession({ token }))) await reload();
  }

  async function revokeOthers() {
    if (await run(() => authClient.revokeOtherSessions())) {
      toast(t('auth.security.othersSignedOut'));
      await reload();
    }
  }

  // NOT FRESH: the server will not LIST other sessions until a recent sign-in,
  // but it will still END them (`revoke-other-sessions` only needs a live
  // session). So the honest screen is this device, why the rest is hidden, and
  // the action the user most likely came for.
  const rows: SessionRow[] =
    state.kind === 'list'
      ? state.sessions
      : state.kind === 'reauth' && data?.session
        ? [
            {
              id: data.session.id,
              token: data.session.token,
              userAgent: data.session.userAgent,
              createdAt: data.session.createdAt,
            },
          ]
        : [];

  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-medium">{t('auth.security.devicesTitle')}</h3>
      {/* The list scrolls in place past ~4 rows; the revoke-all button and
          the sections below stay put instead of the whole dialog scrolling. */}
      {rows.length > 0 && (
        <div className="flex max-h-56 flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
          {rows.map((s) => (
            <div key={s.id} className="flex shrink-0 items-center justify-between">
              <div>
                <p className="text-sm">
                  {parseUserAgent(s.userAgent)}
                  {s.token === currentToken && (
                    <span className="ms-2 rounded bg-muted px-1.5 py-0.5 text-xs">
                      {t('auth.security.currentDevice')}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(s.createdAt).toLocaleDateString()}
                </p>
              </div>
              {s.token !== currentToken && (
                <Button
                  variant="secondary"
                  loading={busy}
                  disabled={busy}
                  onPress={() => void revoke(s.token)}>
                  {t('auth.security.revoke')}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {state.kind === 'reauth' && (
        <p className="text-sm text-muted-foreground" data-testid="devices-reauth">
          {t('auth.security.devicesReauth')}
        </p>
      )}
      {(state.kind === 'error' || state.kind === 'signed-out') && (
        <div
          className="flex items-center justify-between gap-2"
          data-testid="devices-error">
          <p className="text-sm text-muted-foreground">
            {t('auth.security.devicesLoadFailed')}
          </p>
          <Button variant="secondary" onPress={() => void reload()}>
            {t('auth.security.devicesRetry')}
          </Button>
        </div>
      )}
      {((state.kind === 'list' && state.sessions.length > 1) ||
        state.kind === 'reauth') && (
        <Button
          variant="secondary"
          loading={busy}
          disabled={busy}
          onPress={() => void revokeOthers()}>
          {t('auth.security.revokeAll')}
        </Button>
      )}
      {errorKey ? (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      ) : null}
    </section>
  );
}

function DangerSection({ email, hasPassword }: { email: string; hasPassword: boolean }) {
  const { t } = useTranslation();
  const { busy, errorKey, run } = useAuthAction();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  // Collapsed by default: the confirm form is tall enough to
  // push the whole dialog into scrolling, and it's a destructive one-off.
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (hasPassword) {
      // Credential users: deleteUser with the current password deletes
      // immediately (Task 2's server config), so tear down the local
      // session state and bounce home right away.
      const ok = await run(() => authClient.deleteUser({ password }));
      if (ok) {
        clearSessionToken();
        window.location.assign('/');
      }
    } else {
      // OAuth-only: server emails a confirmation link (Task 2's
      // sendDeleteAccountVerification); nothing is deleted until clicked,
      // so the current session stays valid — just show the "check your
      // inbox" state.
      const ok = await run(() => authClient.deleteUser({ callbackURL: '/' }));
      if (ok) setLinkSent(true);
    }
  }

  const confirmed = confirmEmail.trim().toLowerCase() === email.toLowerCase();

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-destructive">{t('auth.security.dangerTitle')}</h3>
        {!confirmOpen && !linkSent && (
          <Button variant="alert" onPress={() => setConfirmOpen(true)}>
            {t('auth.security.deleteAction')}
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{t('auth.security.dangerBody')}</p>
      {linkSent ? (
        <p className="text-sm">{t('auth.security.deleteEmailSent')}</p>
      ) : confirmOpen ? (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <Input
            label={t('auth.security.deleteConfirmLabel')}
            value={confirmEmail}
            onChangeText={setConfirmEmail}
            webProps={{ type: 'email', required: true }}
          />
          {hasPassword && (
            <Input
              label={t('auth.security.deletePasswordLabel')}
              value={password}
              onChangeText={setPassword}
              webProps={{
                type: 'password',
                autoComplete: 'current-password',
                required: true,
              }}
            />
          )}
          <Button
            variant="alert"
            fullWidth
            loading={busy}
            disabled={busy || !confirmed || (hasPassword && !password)}
            webProps={{ type: 'submit' }}>
            {t('auth.security.deleteAction')}
          </Button>
        </form>
      ) : null}
      {errorKey ? (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      ) : null}
    </section>
  );
}

/** Heading-width bar + button-sized block, matching the real section
 * headers (password / connected accounts) so the loading state occupies the
 * same vertical space as the loaded one. */
function SectionHeaderSkeleton() {
  return (
    <div className="flex items-center justify-between">
      <Skeleton shape="line" className="h-5 w-28" />
      <Skeleton className="h-10 w-32" />
    </div>
  );
}

function DeviceRowSkeleton() {
  return (
    <div className="flex shrink-0 items-center justify-between">
      <div className="flex flex-col gap-1.5">
        <Skeleton shape="line" className="w-36" />
        <Skeleton shape="line" className="h-3 w-16" />
      </div>
      <Skeleton className="h-10 w-24" />
    </div>
  );
}

export function AccountSecurityTab() {
  const { t } = useTranslation();
  const { data } = authClient.useSession();
  const { accounts, reload } = useAccounts();
  const email = data?.user.email ?? '';

  if (!data || accounts === null) {
    // Placeholder mirroring the loaded layout (password, connected accounts,
    // devices) so the dialog opens at a stable size instead of the tab
    // popping in when `listAccounts()` resolves.
    return (
      <SkeletonPulse className="gap-4 py-2" accessibilityLabel={t('common.loading')}>
        <SectionHeaderSkeleton />
        <Separator />
        <Skeleton shape="line" className="h-5 w-40" />
        <SectionHeaderSkeleton />
        <SectionHeaderSkeleton />
        <Separator />
        <Skeleton shape="line" className="h-5 w-24" />
        {[0, 1, 2].map((i) => (
          <DeviceRowSkeleton key={i} />
        ))}
      </SkeletonPulse>
    );
  }
  const hasPassword = accounts.some((a) => a.providerId === 'credential');

  return (
    <div className="flex flex-col gap-4 py-2">
      <PasswordSection
        hasPassword={hasPassword}
        email={email}
        onPasswordSet={() => void reload()}
      />
      <Separator />
      <ConnectedAccountsSection
        accounts={accounts}
        hasPassword={hasPassword}
        reload={reload}
      />
      <Separator />
      <DevicesSection />
      <Separator />
      <DangerSection email={email} hasPassword={hasPassword} />
    </div>
  );
}
